import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Snackbar,
  useMediaQuery
} from "@material-ui/core";
import { Product } from "../../../models/Product";
import * as StoreApi from "../../../api/storeApi";
import { Store } from "../../../models/Store";
import { isOk } from "../../../result";
import useStyles from "./styles";
import { useTheme } from "@material-ui/core/styles";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { Alert } from "@material-ui/lab";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store";
import { thunkFetchStores } from "../../../store/stores/actions";
import * as InventoryApi from "../../../api/storeInventoryApi";
import * as StoreInventoryApi from "../../../api/storeInventoryApi";
import { StoreInventoryItemEditor } from "../../stores/StoreList/components/StoreInventoryItemEditor";
import { UserContext } from "../../../UserProvider";
import { Authenticated, isProductCampaignReadOnly } from "../../../models/User";
import { ConnectToPriceTag } from "./ConnectToPriceTag";
import MaterialTable from "@material-table/core";
import StoreIcon from "@material-ui/icons/Store";
import { SectionCard } from "../components/SectionCard";
import { CheckCircle, Clear } from "@material-ui/icons";
import { useI18n } from "../../../hooks/useI18n";

interface StoreTableProps {
  product: Product;
}

interface ProductStores {
  stores: any[];
  isLoading: boolean;
}

export const StoreTable: FunctionComponent<StoreTableProps> = ({ product }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const currentUser = useContext(UserContext) as Authenticated;
  const { t } = useI18n();
  const readOnly = isProductCampaignReadOnly(currentUser);

  const [productStores, setProductStores] = useState<ProductStores>({
    stores: [],
    isLoading: true
  });

  const [storeState, setStoreState] = useState({
    storeId: "",
    storeName: "",
    showDeleteProduct: false,
    showEditProduct: false,
    showConnectPriceTag: false,
    hasProductInInventory: false,
    deleteProductSuccess: false,
    editProductSuccess: false,
    connectedPriceTagSuccess: false,
    reload: false
  });

  const allStores = useSelector<AppState, ReadonlyArray<Store>>(
    s => s.stores.stores
  );

  useEffect(() => {
    (async () => {
      if (storeState.reload) {
        await populateStoresWithProducts();
      }
    })();
  }, [storeState]);

  useEffect(() => {
    (async () => {
      if (!allStores || !allStores.length) {
        dispatch(thunkFetchStores());
      }

      await populateStoresWithProducts();
    })();
  }, []);

  const deleteProductFromStore = async () => {
    const result = await InventoryApi.remove(storeState.storeId, product.id);

    if (isOk(result)) {
      setStoreState({
        ...storeState,
        storeId: "",
        showDeleteProduct: false,
        reload: true,
        deleteProductSuccess: true
      });
    }
  };

  const populateStoresWithProducts = async () => {
    const result = await StoreApi.listByProductId(product.id);
    if (isOk(result)) {
      const storesWithProducts = result.value as Store[];
      let stores = [];

      for (let store of allStores) {
        let available = storesWithProducts.some(s => s.id === store.id);
        stores.push({ ...store, inInventory: available });
      }

      setProductStores({ ...productStores, stores: stores });

      for (let i = 0; i < stores.length; i++) {
        if (stores[i].inInventory) {
          // @ts-ignore
          const response =
            await StoreInventoryApi.getDeliveryStockTakingAndSpoilage(
              stores[i].id,
              product.id
            );
          if (isOk(response)) {
            // @ts-ignore
            stores[i] = { ...stores[i], onShelf: response.value.onShelf };
          }
        }
      }

      setProductStores({ ...productStores, stores: stores, isLoading: false });
    }
    setStoreState({ ...storeState, reload: false });
  };

  return (
    <>
      <SectionCard
        icon={
          <StoreIcon
            style={{
              fontSize: "3rem",
              color: theme.palette.primary.dark
            }}
          />
        }
        title={t("common.stores")}
      >
        <MaterialTable
          isLoading={productStores.isLoading}
          title={t("common.stores")}
          columns={[
            {
              title: t("common.formFields.name"),
              field: "name"
            },
            {
              title: t("common.actions.activate"),
              customSort: (psA, psB) =>
                psA.inInventory && !psB.inInventory ? 1 : -1,
              render: productStore =>
                productStore.inInventory ? (
                  <CheckCircle
                    style={{
                      color: theme.palette.success.dark
                    }}
                  />
                ) : (
                  <Clear
                    style={{
                      color: theme.palette.grey[500]
                    }}
                  />
                )
            },
            {
              title: t("common.formFields.onShelf"),
              field: "onShelf",
              render: productStore =>
                productStore.onShelf != undefined && (
                  <>
                    {productStore.onShelf} {t("common.productDetails.pieces")}
                  </>
                )
            }
          ]}
          data={[...productStores.stores].sort(
            (a: any, b: any) =>
              b.inInventory - a.inInventory || a.name.localeCompare(b.name)
          )}
          components={{
            Container: props => <Paper {...props} elevation={0} />
          }}
          actions={[
            rowData => ({
              hidden:
                readOnly ||
                !currentUser.tenant?.configuration?.features?.digitalPriceTags,
              icon: "link",
              iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
              tooltip: t("common.storeManagement.connectToPriceTagIn", {
                productName: product.name,
                storeName: rowData.name
              }),
              onClick: (event, rowData: any) => {
                if (allStores.length === 1) {
                  const store = allStores[0];
                  setStoreState({
                    ...storeState,
                    showConnectPriceTag: true,
                    hasProductInInventory: rowData.inInventory,
                    storeId: store.id,
                    storeName: store.name
                  });
                } else {
                  setStoreState({
                    ...storeState,
                    showConnectPriceTag: true,
                    hasProductInInventory: rowData.inInventory,
                    storeId: rowData.id,
                    storeName: rowData.name
                  });
                }
              }
            }),
            rowData => ({
              hidden: readOnly || !rowData.inInventory,
              icon: "delete_icon",
              iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
              tooltip: t("common.storeManagement.deactivateIn", {
                productName: product.name,
                storeName: rowData.name
              }),
              onClick: (event, rowData: any) => {
                setStoreState({
                  ...storeState,
                  showDeleteProduct: true,
                  storeId: rowData.id,
                  storeName: rowData.name
                });
              }
            }),
            rowData => ({
              hidden: readOnly || rowData.inInventory,
              icon: "add_circle",
              iconProps: { style: { color: "rgb(31, 132, 52)" } },
              tooltip: t("common.storeManagement.activateIn", {
                productName: product.name,
                storeName: rowData.name
              }),
              onClick: async (event, rowData: any) => {
                if (allStores.length === 1) {
                  const store = allStores[0];

                  if (
                    currentUser.tenant?.configuration?.features
                      ?.digitalPriceTags
                  ) {
                    setStoreState({
                      ...storeState,
                      showEditProduct: true,
                      storeId: store.id,
                      storeName: store.name
                    });
                  } else {
                    const result = await StoreInventoryApi.add(store.id, {
                      productId: product.id,
                      gtin: product.gtin,

                      hasPriceTag: false
                    });

                    if (isOk(result)) {
                      setStoreState({
                        ...storeState,
                        reload: true,
                        editProductSuccess: true
                      });
                    }
                  }
                } else {
                  setStoreState({
                    ...storeState,
                    showEditProduct: true,
                    storeId: rowData.id,
                    storeName: rowData.name
                  });
                }
              }
            })
          ]}
          options={{
            showTitle: false,
            search: false,
            actionsColumnIndex: -1,
            pageSize: allStores.length,
            paging: false
          }}
          localization={{
            header: {
              actions: ""
            }
          }}
        />
      </SectionCard>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        open={storeState.showConnectPriceTag}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setStoreState({ ...storeState, showConnectPriceTag: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <ConnectToPriceTag
            addToStore={!storeState.hasProductInInventory}
            storeId={storeState.storeId}
            gtin={product.gtin}
            productId={product.id}
            onSuccess={() =>
              setStoreState({
                ...storeState,
                showConnectPriceTag: false,
                reload: true,
                connectedPriceTagSuccess: true
              })
            }
            onCancel={() =>
              setStoreState({
                ...storeState,
                showConnectPriceTag: false,
                storeId: ""
              })
            }
          />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={storeState.showEditProduct}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setStoreState({ ...storeState, showEditProduct: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <StoreInventoryItemEditor
            storeId={storeState.storeId}
            inventoryItem={{
              id: "",
              productId: product.id,
              gtin: product.gtin,
              productName: product.name,
              brandName: product.brandName,
              storeId: storeState.storeId,
              storeName: storeState.storeName,

              onShelf: 0,
              onShelfWorth: 0,
              inStorage: 0,
              hasPriceTag: false,
              isOrderable: false,

              created: new Date(),
              updated: new Date(),

              exists: false
            }}
            onSave={(updatedItem: any) => {
              setStoreState({
                ...storeState,
                showEditProduct: false,
                reload: true,
                editProductSuccess: true
              });
            }}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        visible={storeState.showDeleteProduct}
        titleText={t("common.storeManagement.removeFromStore")}
        bodyText={t("common.storeManagement.removeFromStoreConfirmation")}
        confirmText={t("common.actions.remove")}
        onConfirm={() => {
          deleteProductFromStore();
          return true;
        }}
        onCancel={() => {
          setStoreState({ ...storeState, showDeleteProduct: false });
        }}
      />

      <Snackbar
        open={storeState.connectedPriceTagSuccess}
        autoHideDuration={2000}
        onClose={() => {
          setStoreState({
            ...storeState,
            connectedPriceTagSuccess: false
          });
        }}
      >
        <Alert
          onClose={() =>
            setStoreState({
              ...storeState,
              connectedPriceTagSuccess: false
            })
          }
          severity="success"
        >
          {t(
            "common.storeManagement.productAddedInStoreAndConnectingToPricetag"
          )}
        </Alert>
      </Snackbar>

      <Snackbar
        open={storeState.editProductSuccess || storeState.deleteProductSuccess}
        autoHideDuration={2000}
        onClose={() => {
          setStoreState({
            ...storeState,
            editProductSuccess: false,
            deleteProductSuccess: false
          });
        }}
      >
        <Alert
          onClose={() =>
            setStoreState({
              ...storeState,
              editProductSuccess: false,
              deleteProductSuccess: false
            })
          }
          severity="success"
        >
          {storeState.editProductSuccess
            ? t("common.storeManagement.productAddedTo", {
                productName: product.name,
                storeName: storeState.storeName
              })
            : storeState.deleteProductSuccess
            ? t("common.storeManagement.productRemovedFrom", {
                productName: product.name,
                storeName: storeState.storeName
              })
            : ""}
        </Alert>
      </Snackbar>
    </>
  );
};
